import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Send, Copy, X, Clock, Mail, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

// Roles invitable via the RPC. 'owner' is intentionally excluded -- owners
// are set once at create_carrier_with_owner; the DB CHECK constraint enforces
// the same restriction on the server side.
const ROLE_OPTIONS = [
  { value: 'driver',     label: 'Driver' },
  { value: 'dispatch',   label: 'Dispatch' },
  { value: 'hr',         label: 'HR' },
  { value: 'accounting', label: 'Accounting' },
  { value: 'admin',      label: 'Admin' },
];

const ROLE_COLORS = {
  admin:      'text-yellow-700 dark:text-yellow-400 bg-yellow-500/15 dark:bg-yellow-500/10 border-yellow-500/40 dark:border-yellow-500/30',
  hr:         'text-blue-400   bg-blue-500/10   border-blue-500/30',
  accounting: 'text-green-400  bg-green-500/10  border-green-500/30',
  dispatch:   'text-purple-700 dark:text-purple-400 bg-purple-500/15 dark:bg-purple-500/10 border-purple-500/40 dark:border-purple-500/30',
  driver:     'text-teal-400   bg-teal-500/10   border-teal-500/30',
};

const formatRelative = (iso) => {
  if (!iso) return '—';
  const ms = new Date(iso) - Date.now();
  const future = ms > 0;
  const abs = Math.abs(ms);
  const minutes = Math.round(abs / 60000);
  if (minutes < 60) return future ? `in ${minutes}m` : `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return future ? `in ${hours}h` : `${hours}h ago`;
  const days = Math.round(hours / 24);
  return future ? `in ${days}d` : `${days}d ago`;
};

const buildAcceptLink = (token) => `${window.location.origin}/accept-invite?token=${token}`;

export default function InvitationsPanel() {
  const [list, setList]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [email, setEmail]         = useState('');
  const [role, setRole]           = useState('driver');
  const [sending, setSending]     = useState(false);
  const [revokingId, setRevokingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const rows = await client.entities.Invitation.filter(
        { status: 'pending' },
        '-created_date',   // ALIAS maps this to created_at in the compat client
        100,
      );
      setList(rows);
    } catch (e) {
      toast.error('Could not load invitations.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onSend = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) { toast.error('Enter an email.'); return; }
    setSending(true);
    try {
      // RPC returns the new invitations row directly
      const row = await client.rpc('create_invitation', {
        p_email: trimmed,
        p_role:  role,
      });
      // Auto-revoke is handled server-side; the previous pending for this
      // (carrier, email) -- if any -- is already 'revoked' in the DB.
      // Reflect that locally: drop any prior pending row for the same email.
      setList(prev => [row, ...prev.filter(r =>
        r.id !== row.id &&
        r.invited_email.toLowerCase() !== row.invited_email.toLowerCase()
      )]);
      setEmail('');
      // Auto-copy the accept link so admins can share immediately even if the
      // Edge Function email hits spam or a delivery delay.
      try {
        await navigator.clipboard.writeText(buildAcceptLink(row.token));
        toast.success(`Invite sent to ${trimmed} — accept link copied to clipboard.`);
      } catch {
        toast.success(`Invite sent to ${trimmed}.`);
      }
    } catch (err) {
      toast.error(err?.message || 'Could not send invitation.');
    } finally {
      setSending(false);
    }
  };

  const onCopy = async (row) => {
    const link = buildAcceptLink(row.token);
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Accept link copied to clipboard.');
    } catch {
      // Clipboard API can be blocked in some contexts -- fall back to visible link.
      toast.error(`Could not copy. Link: ${link}`);
    }
  };

  const onRevoke = async (row) => {
    if (!window.confirm(`Revoke invitation to ${row.invited_email}? Their accept link will stop working immediately.`)) return;
    setRevokingId(row.id);
    try {
      await client.rpc('revoke_invitation', { p_invitation_id: row.id });
      setList(prev => prev.filter(r => r.id !== row.id));
      toast.success(`Invitation to ${row.invited_email} revoked.`);
    } catch (err) {
      toast.error(err?.message || 'Could not revoke invitation.');
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold text-foreground">Invitations</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Invite teammates or drivers by email. A branded invite link is emailed automatically and copied to your clipboard.
          Invites expire in 7 days; revoking one disables its link immediately.
        </p>
      </div>

      {/* ----- Send invite form ----- */}
      <form onSubmit={onSend} className="glass-card p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px_auto] gap-3 sm:items-end">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              placeholder="teammate@company.com"
              autoComplete="off"
              className="w-full bg-transparent border border-input rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Role</label>
            <select
              value={role}
              onChange={e => setRole(e.target.value)}
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
            >
              {ROLE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <Button type="submit" disabled={sending} className="bg-primary text-primary-foreground h-[42px] px-4">
            {sending
              ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sending…</>
              : <><Send className="w-4 h-4 mr-1" /> Send invite</>}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Accept link format:{' '}
          <span className="font-mono text-foreground/70">{window.location.origin}/accept-invite?token=…</span>
        </p>
      </form>

      {/* ----- Pending invites table ----- */}
      <div>
        <h4 className="text-sm font-semibold text-foreground mb-3">
          Pending invitations
          {list.length > 0 && <span className="text-muted-foreground font-normal"> ({list.length})</span>}
        </h4>
        <div className="glass-card overflow-hidden">
          {loading ? (
            <div className="p-8 flex justify-center">
              <div className="w-6 h-6 border-2 border-border border-t-primary rounded-full animate-spin" />
            </div>
          ) : list.length === 0 ? (
            <div className="p-10 text-center">
              <Mail className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-foreground font-medium">No pending invitations</p>
              <p className="text-xs text-muted-foreground mt-1">Send one using the form above to add a seat to this carrier.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-4 py-3 text-xs text-muted-foreground font-medium">Email</th>
                  <th className="text-left px-4 py-3 text-xs text-muted-foreground font-medium">Role</th>
                  <th className="text-left px-4 py-3 text-xs text-muted-foreground font-medium">Expires</th>
                  <th className="text-left px-4 py-3 text-xs text-muted-foreground font-medium">Sent</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {list.map(row => (
                  <tr key={row.id}>
                    <td className="px-4 py-3 text-foreground">{row.invited_email}</td>
                    <td className="px-4 py-3">
                      <span className={cn(
                        "text-xs font-semibold px-2 py-0.5 rounded-full border capitalize",
                        ROLE_COLORS[row.invited_role] || 'text-muted-foreground bg-muted/40 border-border'
                      )}>
                        {row.invited_role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3 inline mr-1" />
                      {formatRelative(row.expires_at)}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {formatRelative(row.created_date || row.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3 justify-end">
                        <button
                          onClick={() => onCopy(row)}
                          className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                          title="Copy accept link"
                        >
                          <Copy className="w-3.5 h-3.5" /> Copy link
                        </button>
                        <button
                          onClick={() => onRevoke(row)}
                          disabled={revokingId === row.id}
                          className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
                          title="Revoke invitation"
                        >
                          <X className="w-3.5 h-3.5" />
                          {revokingId === row.id ? 'Revoking…' : 'Revoke'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
